import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  Grid,
  TextField,
} from "@mui/material";
import LabelPrinter from "../../../../../../../../components/LabelPrinter";
import {
  formatarInputValorMonetário,
  formatarMonetárioParaNúmero,
  formatarValorMonetario,
} from "../../../../../../../../helpers/masks";
import { useState } from "react";
import { ContaContabil } from "../../../../../../../../types/api/geral/pesquisar-plano-contas";
import { useFormContext } from "react-hook-form";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SelectPlanoContas from "../../../../../../../../components/SelectsSimpleApiQuery/SelectPlanoContas";
import { enqueueSnackbar } from "notistack";
import { IDataCentroCustos } from "../../../../../../../../types/api/contabilidade/pesquisar-centro-custos";
import SelectCentroCusto from "../../../../../../../../components/SelectsSimpleApiQuery/SelectCentroCusto";

export default function DialogAdicionar({
  open,
  handleClose,
}: DialogAdicionarProps) {
  const { getValues, setValue } = useFormContext();
  const prevRateios = getValues("step04.rateios");
  const valorTitulos = getValues("step02.valor_total");

  const [valores, setValores] = useState<ValoresRateio[]>(
    prevRateios.length > 0 ? prevRateios : []
  );

  const [numLinhas, setNumLinhas] = useState<string>("1");
  const [contaPadrao, setContaPadrao] = useState<ContaContabil | null>(null);

  //funcoes manipuladoras

  // Criar linhas automáticas com rateio
  const handleGerarRateio = () => {
    const numeroLinhas = Number(numLinhas);
    if (numeroLinhas <= 0) {
      enqueueSnackbar("Informe um número válido de linhas", {
        variant: "error",
      });
      return;
    }

    const valorTituloNum = formatarMonetárioParaNúmero(valorTitulos) || 0;
    const valorBase = Math.floor((valorTituloNum / numeroLinhas) * 100) / 100;
    const linhas: ValoresRateio[] = [];

    for (let i = 0; i < numeroLinhas; i++) {
      linhas.push({
        contaDevedora: contaPadrao,
        valor: formatarValorMonetario(valorBase),
      });
    }

    //Arredondamento na ultima linha
    const totalRateado = valorBase * numeroLinhas;
    const diferenca = valorTituloNum - totalRateado;
    if (diferenca !== 0) {
      const ultimaLinha = linhas[linhas.length - 1];
      const valorUltimaLinha =
        formatarMonetárioParaNúmero(ultimaLinha.valor) + diferenca;
      ultimaLinha.valor = formatarValorMonetario(valorUltimaLinha);
    }

    setValores(linhas);
  };

  const handleChangeCentroCusto = (
    index: number,
    value: IDataCentroCustos | null
  ) => {
    const novasLinhas = [...valores];
    if (novasLinhas[index].contaDevedora) {
      novasLinhas[index].contaDevedora = {
        ...novasLinhas[index].contaDevedora!,
        centro_custo: value,
      };
    }
    setValores(novasLinhas);
  };

  const handleRemoveLinha = (index: number) => {
    const novasLinhas = valores.filter((_, idx) => idx !== index);
    setValores(novasLinhas);
  };

  const handleChangeContaDevedora = (
    index: number,
    value: ContaContabil | null
  ) => {
    const novasLinhas = [...valores];

    novasLinhas[index].contaDevedora = value;

    if (novasLinhas[index].contaDevedora) {
      novasLinhas[index].contaDevedora = {
        ...novasLinhas[index].contaDevedora!,
        centro_custo: null,
      };
    }

    setValores(novasLinhas);
  };

  const handleChangeValorRateio = (index: number, value: string) => {
    const novasLinhas = [...valores];
    novasLinhas[index].valor = value;
    setValores(novasLinhas);
  };

  const handleSubmit = () => {
    const temContaDevedoraNula = valores.some(
      (item) => item.contaDevedora === null
    );

    if (temContaDevedoraNula) {
      enqueueSnackbar("Todas as contas devedoras devem ser preenchidas", {
        variant: "error",
      });
      return;
    }

    const temValorInvalido = valores.some((item) => {
      const valor = formatarMonetárioParaNúmero(item.valor);
      return !valor || valor <= 0;
    });

    if (temValorInvalido) {
      enqueueSnackbar(
        "Todos os valores devem ser preenchidos e maiores que zero",
        { variant: "error" }
      );
      return;
    }

    setValue("step04.rateios", valores);
    handleClose();
  };
  //

  //valores importantes
  const totalRateio = valores.reduce(
    (acc, curr) => acc + (formatarMonetárioParaNúmero(curr.valor) || 0),
    0
  );

  const valorTituloNumero = formatarMonetárioParaNúmero(valorTitulos) || 0;
  const valorRestante = valorTituloNumero - totalRateio;
  //

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Rateio</DialogTitle>

      <DialogContent>
        <Grid mb={3} mt={0.7} container spacing={2} alignItems="center">
          <Grid size={{xs: 4}}>
            <TextField
              label="Nº de linhas"
              type="number"
              fullWidth
              value={numLinhas}
              onChange={(e) => setNumLinhas(e.target.value)}
            />
          </Grid>
          <Grid size={{xs: 7}}>
            <SelectPlanoContas
              inputLabel="Conta padrão (opcional)"
              value={contaPadrao}
              onChangeValue={(valor) => setContaPadrao(valor as ContaContabil)}
              extraParams={{ tipo: "Analítica" }}
              hasCentroCusto={false}
            />
          </Grid>
          <Grid size={{xs: 1}}>
            <Box height={"100%"}>
              <Button
                variant="contained"
                fullWidth
                onClick={handleGerarRateio}
                sx={{ height: "100%" }}
              >
                Gerar
              </Button>
            </Box>
          </Grid>
        </Grid>

        <Box mt={1}>
          {valores.map((linha, index) => (
            <Grid container spacing={2} key={index} alignItems="center" mb={2}>
              <Grid size={{xs: 4.3}}>
                <SelectPlanoContas
                  inputLabel="Conta devedora"
                  required
                  value={linha.contaDevedora}
                  onChangeValue={(valor) =>
                    handleChangeContaDevedora(index, valor as ContaContabil)
                  }
                  extraParams={{ tipo: "Analítica" }}
                  hasCentroCusto={false}
                />
              </Grid>

              <Grid size={{xs: 12, md: 4.3}}>
                {linha.contaDevedora?.centro_custos ? (
                  <SelectCentroCusto
                    value={linha.contaDevedora?.centro_custo || null}
                    onChange={(value) => handleChangeCentroCusto(index, value)}
                    disabled={false}
                    extraParams={{
                      centro_custos: linha.contaDevedora?.centro_custos?.map(
                        (centro) => centro.id
                      ),
                    }}
                  />
                ) : (
                  <TextField
                    label="Centro de custo fiscal"
                    value="Não possui centro de custo"
                    variant="outlined"
                    fullWidth
                    disabled
                  />
                )}
              </Grid>

              <Grid size={{xs: 2.5}}>
                <TextField
                  fullWidth
                  label="Valor"
                  value={linha.valor}
                  onChange={(e) =>
                    handleChangeValorRateio(
                      index,
                      formatarInputValorMonetário(e.target.value)
                    )
                  }
                />
              </Grid>

              <Grid size={{xs: 2.5}}>
                {valores.length >= 1 && (
                  <Button
                    color="error"
                    variant="text"
                    onClick={() => handleRemoveLinha(index)}
                    sx={{ height: "56px" }}
                  >
                    <DeleteOutlineIcon />
                  </Button>
                )}
              </Grid>
            </Grid>
          ))}
        </Box>
      </DialogContent>

      <Stack
        p={3}
        direction="row"
        justifyContent="space-between"
        alignItems="flex-end"
      >
        <Stack direction={"row"} spacing={3}>
          <LabelPrinter
            size="small"
            valor={valorTitulos}
            titulo="Valor do título"
          />
          <LabelPrinter
            size="small"
            titulo="Total rateio"
            valor={formatarValorMonetario(totalRateio)}
            color={
              valorTitulos === formatarValorMonetario(totalRateio)
                ? "primary"
                : "error"
            }
          />
          <LabelPrinter
            size="small"
            titulo="Valor restante"
            valor={formatarValorMonetario(valorRestante)}
          />
        </Stack>

        <Stack direction="row" spacing={1}>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button
            autoFocus
            variant="contained"
            onClick={handleSubmit}
            disabled={valorTitulos !== formatarValorMonetario(totalRateio)}
          >
            Salvar
          </Button>
        </Stack>
      </Stack>
    </Dialog>
  );
}

interface DialogAdicionarProps {
  open: boolean;
  handleClose: () => void;
}

export type ValoresRateio = {
  contaDevedora: ContaContabil | null;
  valor: string;
};
